import React, { ReactElement } from "react";
import * as O from "fp-ts/Option";
import { pipe } from "fp-ts/function";

import { ReportEdge } from "../generated/types";

type RenderItem<T> = (item: T) => ReactElement | null;

export const reportsListRender = (
    items: Array<ReportEdge>,
    renderSingleTemplate: RenderItem<ReportEdge>,
    renderCombinedTemplate: RenderItem<ReportEdge>,
): React.ReactElement[] => {
    // Sort items lexicographically by year
    const sortedItems = [...items].sort((a: any, b: any) => {
        const yearA = a?.year || "";
        const yearB = b?.year || "";
        return yearB.localeCompare(yearA); // B -> A reverses order, makes years descending
    });

    // extract first item from array
    const firstItem: ReportEdge | null = sortedItems[0] ?? null;

    // Apply renderSingleTemplate only to non-null items
    const elements: React.ReactElement[] = sortedItems.reduce((acc: React.ReactElement[], item) => {
        const el = pipe(
            item,
            O.fromNullable,
            O.fold(
                () => null, // If no item, return null
                renderSingleTemplate, // If an item exists, render it
            ),
        );

        if (el !== null) {
            acc.push(el);
        }

        return acc;
    }, []);

    // Ensure firstItem is not null before unshifting
    pipe(
        firstItem,
        O.fromNullable,
        O.fold(
            () => null, // If no first item, do nothing
            (firstItem) => elements.unshift(renderCombinedTemplate(firstItem) as React.ReactElement), // If first item exists, unshift
        ),
    );

    return elements;
};
